import React from "react";
import { Users, Layers, Clock, ChevronRight, ChevronLeft, BookOpen, FileText, Check, Search, Wrench, AlertTriangle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { DASHBOARD_BASE } from "../../../../../config/featureFlags";
import { fmtMins } from "../../../components/classic/micro";
import { fmtClock } from "../../workSummaryNew/bits";
import { useSurface } from "../../growthSurface";
import { GPanel } from "../parts";
import { InfoDot } from "../../../components/classic/InfoDot";
import { inks, paper } from "../viz";
import { Avatar } from "../../../components/primitives";
import { WorkWriteup } from "./WorkWriteup";
import { BeatList, SkeletonRows, WaitLine, WorkRows, splitBeats } from "./StoryChrome";
import { TimeRail, railFromSittings } from "./TimelineKit";

function shortDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return String(iso || "");
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function cleanLine(s) {
  return String(s || "").replace(/\s+and\s+\d+\s+more\s+pages?$/i, "").replace(/\s+/g, " ").trim();
}

const clamp2 = {
  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

function SectionBar({ t, tone, icon, title, bits, info }) {
  const ink = inks(t)[tone] || inks(t).blue;
  const ppr = paper(t);
  const band = `color-mix(in srgb, ${ink} ${t.isDark ? "20%" : "11%"}, ${ppr.card})`;
  return (
    <div style={{
      margin: "-17px -18px 14px",
      padding: "12px 16px",
      background: band,
      borderBottom: `1px solid ${t.c.border}`,
      borderRadius: `${t.r.card} ${t.r.card} 0 0`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span aria-hidden="true" style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          display: "grid", placeItems: "center", background: ink, color: "#fff",
        }}>{icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px", color: t.c.text }}>{title}</h3>
          {bits?.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 3 }}>
              {bits.map((b) => (
                <span key={b.label} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  fontSize: 12, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums",
                }}>
                  <span style={{ display: "grid" }}>{b.icon}</span>
                  {b.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {info ? <InfoDot metric={info} tone="graphical" /> : null}
      </div>
    </div>
  );
}

function fmtDay(range) {
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (raw) => {
    const d = raw instanceof Date ? raw : new Date(raw);
    if (!Number.isFinite(d.getTime())) return "";
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  return { startDate: iso(range?.start), endDate: iso(range?.end) };
}

/* ── the zoom instrument's raw material ──────────────────────────────────
   Sittings are the only per-block time data the project streams carry:
   each one has a real fetched startMs/endMs. Everything below maps those
   — and ONLY those — into instrument segments. Nothing is invented. */

const SEG_INK_NAMES = ["blue", "violet", "gold", "pink"];

/** Stable, linkable identity for one sitting: who + when it started. */
export function sitId(st) {
  return `${st?.userId ?? "u"}.${st?.startMs ?? 0}`;
}

export function sitMinutes(st) {
  if (st?.hops?.length > 1) {
    return st.hops.reduce((n, s) => n + sitMinutes({ ...s, hops: null }), 0);
  }
  const m = Number(st?.minutes);
  return m > 0 ? m : 0;
}

/** Clocks only when the sitting is one real block. No first→last glue, no wall-span minutes. */
export function honestFace(st) {
  const hops = st?.hops?.length ? st.hops : [st];
  const a = Number(hops[0]?.startMs);
  const b = Number((hops[hops.length - 1] || st)?.endMs);
  const startOk = Number.isFinite(a) && a > 1e11;
  const endOk = Number.isFinite(b) && b > a;
  const tracked = hops.reduce((n, s) => n + (Number(s?.minutes) > 0 ? Number(s.minutes) : 0), 0);
  const wall = startOk && endOk ? Math.round((b - a) / 60000) : 0;
  const rangeOk = hops.length === 1 && tracked > 0 && wall > 0
    && wall <= 150
    && Math.abs(wall - tracked) <= Math.max(12, Math.round(tracked * 0.2));
  const startShow = rangeOk && startOk;
  return {
    hm: startShow ? sitClockHM(a) : "",
    mins: rangeOk ? tracked : 0,
    range: rangeOk ? `${fmtClock(a)} → ${fmtClock(b)}` : "",
    visits: hops.length,
  };
}

function sittingMeta(st) {
  const face = honestFace(st);
  return [
    face.range,
    face.visits > 1 ? `${face.visits} visits` : null,
    st.name ? firstName(st.name) : null,
    st.day ? shortDay(st.day) : null,
  ].filter(Boolean).join(" · ");
}

function sitClockHM(ms) {
  const d = new Date(Number(ms));
  if (!Number.isFinite(d.getTime())) return "—";
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "They";
}

function workLogTo(userId, day) {
  const qs = new URLSearchParams();
  if (userId) qs.set("userId", String(userId));
  if (day) qs.set("date", day);
  qs.set("tab", "insights");
  return `${DASHBOARD_BASE}/work-summary-new?${qs}`;
}

function LogDoorBtn({ t, href, title, sub }) {
  const navigate = useNavigate();
  const k = inks(t);
  return (
    <button type="button" onClick={() => navigate(href)} style={{
      display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
      padding: "15px 14px", borderRadius: 14, cursor: "pointer",
      fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
      boxShadow: `0 8px 20px -10px ${k.blue}`,
    }}>
      <span aria-hidden="true" style={{
        width: 40, height: 40, borderRadius: 11, flexShrink: 0,
        display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
      }}><BookOpen size={18} strokeWidth={2.2} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 650, letterSpacing: "-0.2px" }}>{title}</span>
        <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>{sub}</span>
      </span>
      <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
    </button>
  );
}

function textOf(item) {
  return typeof item === "string" ? item : String(item?.text || "");
}

function tokensOf(s) {
  return new Set(String(s || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3));
}

function overlapOf(a, b) {
  const A = tokensOf(a);
  const B = tokensOf(b);
  if (!A.size || !B.size) return 0;
  let n = 0;
  for (const w of A) if (B.has(w)) n += 1;
  return n / Math.min(A.size, B.size);
}

function inferKind(text) {
  const s = String(text || "").toLowerCase();
  if (/still|left|open|missing|unclear|not yet|remain/.test(s)) return "open";
  if (/found|saw|noticed|showed|revealed/.test(s)) return "found";
  if (/decid|chose|picked|went with/.test(s)) return "decided";
  return "did";
}

function insightIcon(kind) {
  if (kind === "found") return Search;
  if (kind === "decided") return Wrench;
  if (kind === "open") return AlertTriangle;
  return Check;
}

function pickInsights(headline, beats) {
  const raw = (beats || []).flatMap((b) => {
    const text = textOf(b);
    const kind = (typeof b === "object" && b?.kind) || inferKind(text);
    return splitBeats(text).map((line) => ({ text: line, kind }));
  });
  const out = [];
  const seen = [headline];
  for (const row of raw) {
    if (!row.text) continue;
    if (seen.some((s) => overlapOf(s, row.text) >= 0.48)) continue;
    seen.push(row.text);
    out.push(row);
    if (out.length >= 4) break;
  }
  return out;
}

function GlancePoints({ t, headline, beats }) {
  const k = inks(t);
  const ppr = paper(t);
  const rows = pickInsights(headline, beats);
  if (!headline && !rows.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      {headline ? (
        <p style={{ margin: "0 0 14px", fontSize: 16.5, fontWeight: 650, lineHeight: 1.35, letterSpacing: "-0.25px" }}>{headline}</p>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((row, i) => {
          const Icon = insightIcon(row.kind);
          const ink = row.kind === "open" ? k.gold : k.violet;
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "32px 1fr", gap: 12, alignItems: "center",
              padding: "11px 12px", borderRadius: 12,
              background: t.isDark ? ppr.well : "#fff",
              boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
            }}>
              <span aria-hidden="true" style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                display: "grid", placeItems: "center", background: ink, color: "#fff",
              }}><Icon size={15} strokeWidth={2.2} /></span>
              <span style={{ fontSize: 14, fontWeight: 550, lineHeight: 1.4, letterSpacing: "-0.15px" }}>{row.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OutcomeGlance({ t, outcomes, insights }) {
  const k = inks(t);
  const ppr = paper(t);
  const texts = (insights || []).map((x) => textOf(x));
  const list = (outcomes || []).filter((o) => o && o.text && !texts.some((s) => overlapOf(s, o.text) >= 0.45)).slice(0, 2);
  if (!list.length) return null;
  const meta = {
    done: { Icon: Check, tone: "blue", label: "Done" },
    progress: { Icon: Clock, tone: "gold", label: "In progress" },
    open: { Icon: AlertTriangle, tone: "violet", label: "Still open" },
  };
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${list.length}, minmax(0, 1fr))`,
      gap: 6, marginBottom: 14,
    }}>
      {list.map((o, i) => {
        const m = meta[o.state] || meta.done;
        const ink = k[m.tone] || k.blue;
        return (
          <div key={i} style={{
            padding: "10px 12px", borderRadius: 12,
            background: t.isDark ? ppr.well : "#fff",
            boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span aria-hidden="true" style={{
                width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                display: "grid", placeItems: "center", background: ink, color: "#fff",
              }}><m.Icon size={12} strokeWidth={2.4} /></span>
              <span style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>{m.label}</span>
            </span>
            <span style={{ display: "block", marginTop: 6, fontSize: 13, fontWeight: 550, lineHeight: 1.4 }}>{o.text}</span>
          </div>
        );
      })}
    </div>
  );
}

function foldSits(rows) {
  const map = new Map();
  const order = [];
  for (const st of rows || []) {
    const key = cleanLine(st.what || st.title).toLowerCase().slice(0, 48) || sitId(st);
    if (!map.has(key)) { map.set(key, []); order.push(key); }
    map.get(key).push(st);
  }
  return order.map((key) => {
    const hops = map.get(key).sort((a, b) => (Number(a.startMs) || 0) - (Number(b.startMs) || 0));
    const last = hops[hops.length - 1];
    return {
      ...hops[0],
      hops,
      endMs: last?.endMs || hops[0].endMs,
      minutes: hops.reduce((n, s) => n + sitMinutes(s), 0),
      visits: hops.length,
    };
  });
}

function SittingOverview({ t, rows, inkMap, headline, beats }) {
  const k = inks(t);
  const ppr = paper(t);
  const groups = [...foldSits(rows)].sort((a, b) => sitMinutes(b) - sitMinutes(a));
  const insights = pickInsights(headline, beats);
  const shown = insights.length ? groups.slice(0, 1) : groups.slice(0, 2);
  if (!shown.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {shown.map((st, i) => {
        const ink = k[(inkMap && inkMap[sitId(st)]) || ["blue", "violet", "gold", "pink"][i % 4]] || k.blue;
        const face = honestFace(st);
        const title = cleanLine(st.what || st.title || st.app) || "This sitting";
        const lines = i === 0 ? insights : (st.overview || []).slice(0, 3);
        const punch = i === 0 ? headline : null;
        return (
          <div key={sitId(st) || i} style={{
            borderRadius: 14,
            background: t.isDark ? ppr.well : "#fff",
            boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
            padding: "12px 12px 12px",
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "48px 1fr", gap: 12, alignItems: "center" }}>
              <span aria-hidden="true" style={{
                width: 48, height: 48, borderRadius: 11, flexShrink: 0,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                background: ink, color: "#fff", gap: 1,
              }}>
                <span style={{
                  fontSize: 13, fontWeight: 750, fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.4px", lineHeight: 1,
                }}>{face.hm || title.slice(0, 2)}</span>
                <span style={{ fontSize: 10, fontWeight: 650, opacity: 0.82, lineHeight: 1.2 }}>
                  {face.mins ? fmtMins(face.mins) : ""}
                </span>
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 650, letterSpacing: "-0.2px" }}>{title}</span>
                <span style={{
                  display: "block", marginTop: 4, fontSize: 12.5, color: t.c.textSecondary,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {sittingMeta(st)}
                </span>
              </span>
            </div>
            {punch ? (
              <p style={{ margin: "12px 0 0", fontSize: 14.5, fontWeight: 650, lineHeight: 1.4, letterSpacing: "-0.2px" }}>{punch}</p>
            ) : null}
            {lines.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: punch ? 10 : 12 }}>
                {lines.map((row, li) => {
                  const text = textOf(row);
                  const kind = row.kind || inferKind(text);
                  const Icon = insightIcon(kind);
                  const tone = kind === "open" ? k.gold : k.violet;
                  return (
                    <div key={li} style={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 10, alignItems: "center" }}>
                      <span aria-hidden="true" style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        display: "grid", placeItems: "center", background: tone, color: "#fff",
                      }}><Icon size={13} strokeWidth={2.2} /></span>
                      <span style={{ fontSize: 13.5, fontWeight: 550, lineHeight: 1.4 }}>{text}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ margin: "10px 0 0", fontSize: 13, color: t.c.textSecondary, lineHeight: 1.45 }}>
                Time is real. Nothing distinct enough to write up in this sitting.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SittingTiles({ t, rows, onOpen, inkMap, currentId }) {
  const k = inks(t);
  const ppr = paper(t);
  const list = foldSits(rows);
  if (!list.length) return null;
  return (
    <div className="tgw-tl tgw-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {list.map((st, i) => {
        const hops = st.hops || [st];
        const id = sitId(st);
        const on = currentId && hops.some((h) => sitId(h) === currentId);
        const ink = k[(inkMap && inkMap[id]) || ["blue", "violet", "gold", "pink"][i % 4]] || k.blue;
        const face = honestFace(st);
        const line = cleanLine(st.what || st.title) || "A sitting";
        const Tag = onOpen ? "button" : "div";
        return (
          <Tag
            key={id || i}
            type={onOpen ? "button" : undefined}
            className={onOpen ? "tgw-row" : undefined}
            onClick={onOpen ? () => !on && onOpen(hops[0]) : undefined}
            style={{
              display: "grid", gridTemplateColumns: onOpen ? "48px 1fr 18px" : "48px 1fr", gap: 12, alignItems: "center",
              width: "100%", textAlign: "left", border: 0, padding: "11px 12px", borderRadius: 14,
              cursor: onOpen && !on ? "pointer" : "default", fontFamily: "inherit", color: "inherit",
              background: t.isDark ? ppr.well : "#fff",
              boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
              position: "relative",
            }}
          >
            {on ? (
              <i aria-hidden="true" style={{
                position: "absolute", left: 0, top: 10, bottom: 10, width: 3, borderRadius: "0 3px 3px 0", background: k.blue,
              }} />
            ) : null}
            <span className="tgw-tl-card" aria-hidden="true" style={{
              width: 48, height: 48, borderRadius: 11, flexShrink: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              background: ink, color: "#fff", gap: 1,
            }}>
              <span style={{
                fontSize: 13, fontWeight: 750, fontVariantNumeric: "tabular-nums",
                letterSpacing: "-0.4px", whiteSpace: "nowrap", lineHeight: 1,
              }}>{face.hm || line.slice(0, 2)}</span>
              <span style={{ fontSize: 10, fontWeight: 650, opacity: 0.82, lineHeight: 1.2 }}>
                {face.mins ? fmtMins(face.mins) : ""}
              </span>
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{
                ...clamp2, fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px",
                lineHeight: 1.35, color: t.c.text,
              }}>{line}</span>
              <span style={{
                display: "block", marginTop: 5, fontSize: 12.5, color: t.c.textSecondary,
                fontVariantNumeric: "tabular-nums",
              }}>
                {sittingMeta(st)}
              </span>
            </span>
            {onOpen ? <span style={{ color: t.c.textTertiary, display: "grid" }}><ChevronRight size={16} /></span> : null}
          </Tag>
        );
      })}
    </div>
  );
}

/**
 * One ink per person, assigned in order of first appearance, cycling the
 * theme's four inks. Computed once from the SAME base list the segments
 * use, so a sitting's row dot always matches its timeline block.
 */
export function sittingInkMap(sittings) {
  const order = [];
  const map = {};
  for (const s of sittings || []) {
    const u = String(s?.userId ?? "");
    if (!order.includes(u)) order.push(u);
  }
  for (const s of sittings || []) {
    const u = String(s?.userId ?? "");
    map[sitId(s)] = SEG_INK_NAMES[Math.max(0, order.indexOf(u)) % SEG_INK_NAMES.length];
  }
  return map;
}

function minuteOfDay(ms) {
  const d = new Date(Number(ms));
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

/**
 * Real sittings → ZoomInstrument segments (minutes since midnight).
 * If the sittings span more than one day, minutes-since-midnight would
 * lie, so this returns [] and the instrument hides itself — correct for
 * multi-day project ranges.
 */
export function buildSittingSegments(sittings, inkMap) {
  const list = (sittings || []).filter((s) => {
    const a = Number(s?.startMs);
    const b = Number(s?.endMs);
    return Number.isFinite(a) && Number.isFinite(b) && b > a && honestFace(s).range;
  });
  if (!list.length) return [];
  const dayOf = (s) => s.day || new Date(Number(s.startMs)).toDateString();
  if (new Set(list.map(dayOf)).size !== 1) return [];
  const map = inkMap || sittingInkMap(list);
  return list
    .map((s) => {
      const who = String(s.name || "").split(" ")[0];
      const what = s.what || s.title || "A sitting";
      return {
        id: sitId(s),
        s: minuteOfDay(s.startMs),
        e: minuteOfDay(s.endMs),
        ink: map[sitId(s)] || "blue",
        label: `${what}${who ? ` — ${who}` : ""}, ${fmtClock(Number(s.startMs))} to ${fmtClock(Number(s.endMs))}`,
      };
    })
    .filter((g) => g.e > g.s);
}

/**
 * The one drill level that used to be local state only. Now it's the
 * `sit` query param — linkable, refreshable, back-button-safe. Both the
 * frame (for the instrument's viewport) and the story views (for the
 * body) read the same param.
 */
export function useSitParam() {
  const [params, setParams] = useSearchParams();
  const sitKey = params.get("sit");
  const openSit = React.useCallback((st) => {
    const next = new URLSearchParams(params);
    next.set("sit", sitId(st));
    setParams(next);
  }, [params, setParams]);
  const closeSit = React.useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("sit");
    setParams(next);
  }, [params, setParams]);
  return { sitKey, openSit, closeSit };
}

async function readSse(url, body, signal, onEvent) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${SERVER_URL}${url}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
    throw new Error(err?.message || "Could not write this up.");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      let d;
      try { d = JSON.parse(line.slice(6)); } catch (_e) { continue; }
      onEvent(d);
    }
  }
}

export function useProjectStory({ projectId, range, userId }) {
  const [lede, setLede] = React.useState("");
  const [beats, setBeats] = React.useState([]);
  const [themes, setThemes] = React.useState([]);
  const [sittings, setSittings] = React.useState([]);
  const [facts, setFacts] = React.useState(null);
  const [empty, setEmpty] = React.useState("");
  const [error, setError] = React.useState("");
  const [running, setRunning] = React.useState(true);

  React.useEffect(() => {
    if (!projectId || !range?.start) return undefined;
    const ctrl = new AbortController();
    setLede(""); setBeats([]); setThemes([]); setSittings([]); setFacts(null);
    setEmpty(""); setError(""); setRunning(true);
    const { startDate, endDate } = fmtDay(range);
    readSse("/api/v1/classic-dashboard/project-story", {
      projectId, startDate, endDate, userId: userId || undefined,
    }, ctrl.signal, (d) => {
      if (d.type === "facts") setFacts(d.facts);
      else if (d.type === "lede") setLede(d.text);
      else if (d.type === "beat") setBeats((c) => [...c, d.text]);
      else if (d.type === "theme") setThemes((c) => [...c, d]);
      else if (d.type === "sitting") setSittings((c) => [...c, d]);
      else if (d.type === "empty") setEmpty(d.message);
      else if (d.type === "error") setError(d.message);
      else if (d.type === "done") setRunning(false);
    }).catch((e) => {
      if (e.name !== "AbortError") setError(e.message);
    }).finally(() => setRunning(false));
    return () => ctrl.abort();
  }, [projectId, range?.start, range?.end, userId]);

  return { lede, beats, themes, sittings, facts, empty, error, running };
}

export function useProjectTheme({ projectId, range, themeTitle, userId }) {
  const [headline, setHeadline] = React.useState("");
  const [story, setStory] = React.useState([]);
  const [points, setPoints] = React.useState([]);
  const [phases, setPhases] = React.useState([]);
  const [outcomes, setOutcomes] = React.useState([]);
  const [facts, setFacts] = React.useState(null);
  const [empty, setEmpty] = React.useState("");
  const [error, setError] = React.useState("");
  const [running, setRunning] = React.useState(true);

  React.useEffect(() => {
    if (!projectId || !themeTitle) return undefined;
    const ctrl = new AbortController();
    setHeadline(""); setStory([]); setPoints([]); setPhases([]); setOutcomes([]);
    setFacts(null); setEmpty(""); setError(""); setRunning(true);
    const { startDate, endDate } = fmtDay(range);
    readSse("/api/v1/classic-dashboard/project-theme", {
      projectId, startDate, endDate, themeTitle, userId: userId || undefined,
    }, ctrl.signal, (d) => {
      if (d.type === "facts") setFacts(d.facts);
      else if (d.type === "headline") setHeadline(d.text);
      else if (d.type === "story") setStory((c) => [...c, d.text]);
      else if (d.type === "point" || d.type === "insight") setPoints((c) => [...c, { text: d.text, kind: d.kind || null }]);
      else if (d.type === "phase") setPhases((c) => [...c, d]);
      else if (d.type === "outcome") setOutcomes((c) => [...c, d]);
      else if (d.type === "empty") setEmpty(d.message);
      else if (d.type === "error") setError(d.message);
      else if (d.type === "done") setRunning(false);
    }).catch((e) => {
      if (e.name !== "AbortError") setError(e.message);
    }).finally(() => setRunning(false));
    return () => ctrl.abort();
  }, [projectId, range?.start, range?.end, themeTitle, userId]);

  return { headline, story, points, phases, outcomes, facts, empty, error, running };
}

export function StoryLede({ t, lede, beats, running, empty, error, compact = false }) {
  if (error && !lede && !beats?.length) return <div style={{ fontSize: 14, color: t.status?.negative || t.c.textSecondary }}>{error}</div>;
  if (empty && !lede && !beats?.length) return <div style={{ fontSize: 14.5, lineHeight: 1.55, color: t.c.textSecondary }}>{empty}</div>;
  if (!lede && !beats?.length) return <WaitLine t={t} text={running ? "Reading what was actually done…" : "No written picture yet."} />;
  if (compact) {
    const punch = lede || beats[0];
    const rest = (lede ? beats : beats.slice(1)).slice(0, 2);
    return (
      <div>
        <p style={{ margin: 0, fontSize: 15.5, fontWeight: 650, lineHeight: 1.35, letterSpacing: "-0.2px" }}>{punch}</p>
        {rest.map((b, i) => (
          <p key={i} style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.45, color: t.c.textSecondary }}>{b}</p>
        ))}
      </div>
    );
  }
  return <BeatList t={t} beats={beats} text={lede} running={running} />;
}

export function ThemeCards({ t, themes, sittings, onOpen }) {
  return (
    <WorkRows
      t={t}
      onOpen={onOpen}
      rows={themes.map((th) => {
        const n = sittings.filter((s) => s.theme === th.key).length;
        return { key: th.key, title: th.title, detail: th.what, who: th.who, cue: n ? `${n} ›` : "›", ...th };
      })}
    />
  );
}

/**
 * Sitting rows — the WhenWhatTable grammar, plus the row↔timeline
 * cross-light: hovering a row rings its block on the instrument (onHot)
 * and a hovered block lights its row (hotId). Ink dots carry the same
 * per-person ink the segments use.
 */
export function SittingCards({ t, rows, onOpen, inkMap, hotId, onHot }) {
  const list = rows || [];
  if (!list.length) return null;
  /* TimelineKit rail (mock-approved): same rows, same ids (sitId), same
     cross-light contract — hover a card, its block rings on the instrument
     and back. The original sitting travels as row.raw so callers keep
     receiving the exact object they always did. */
  return (
    <TimeRail
      t={t}
      rows={railFromSittings(list, { inkMap, idFor: (st) => sitId(st) })}
      hotId={hotId}
      onHot={onHot}
      onOpen={onOpen ? (row, i) => onOpen(row.raw, i) : undefined}
      gaps={false}
    />
  );
}

export function ProjectThemeView({
  t, projectId, range, themeTitle, name, openMember, onBack,
  theme: themeProp, inkMap,
}) {
  const k = inks(t);
  const ppr = paper(t);
  const sf = useSurface();
  const own = useProjectTheme({ projectId: themeProp ? null : projectId, range, themeTitle });
  const { headline, story, points, outcomes, facts, empty, error, running } = themeProp || own;
  const { sitKey, openSit, closeSit } = useSitParam();
  const sitRows = [...(facts?.sittings || [])].sort((a, b) => (Number(a.startMs) || 0) - (Number(b.startMs) || 0));
  const sit = sitKey ? sitRows.find((s) => sitId(s) === sitKey) || null : null;
  const people = [...(facts?.people || [])].sort((a, b) => (b.minutes || 0) - (a.minutes || 0));
  const beats = points.length ? points : story;
  const lead = people[0] || sit || sitRows[0];
  const logDay = sit?.day || sitRows[0]?.day;
  const logUser = sit?.userId || lead?.userId;

  const jumpSit = (dir) => {
    const i = sitRows.findIndex((s) => sitId(s) === sitKey);
    const next = sitRows[i + dir];
    if (next) openSit(next);
  };

  if (sitKey && running && !sit) {
    return (
      <GPanel t={t} style={{ margin: 0 }}>
        <SectionBar t={t} tone="violet" icon={<Clock size={16} strokeWidth={2.2} />} title="Opening this sitting" info="focus" />
        <SkeletonRows />
      </GPanel>
    );
  }

  if (sit) {
    const local = Math.max(0, sitRows.findIndex((s) => sitId(s) === sitKey));
    const face = honestFace(sit);
    const clock = face.range ? { t0: face.range.split(" → ")[0], t1: face.range.split(" → ")[1], mins: face.mins } : null;
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <button type="button" onClick={closeSit} style={{
            display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
            borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
            color: t.c.text, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 650,
          }}><ChevronLeft size={14} /> {themeTitle || name}</button>
          <span style={{ fontSize: 13, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
            Sitting {sitRows.length ? local + 1 : "—"} of {sitRows.length || "—"}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" disabled={local <= 0} onClick={() => jumpSit(-1)} style={{
            display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
            borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
            color: local <= 0 ? t.c.textTertiary : t.c.text, cursor: local <= 0 ? "default" : "pointer",
            fontFamily: "inherit", fontSize: 12.5, fontWeight: 650, opacity: local <= 0 ? 0.45 : 1,
          }}><ChevronLeft size={14} /> Prev</button>
          <button type="button" disabled={local >= sitRows.length - 1} onClick={() => jumpSit(1)} style={{
            display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
            borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
            color: local >= sitRows.length - 1 ? t.c.textTertiary : t.c.text,
            cursor: local >= sitRows.length - 1 ? "default" : "pointer",
            fontFamily: "inherit", fontSize: 12.5, fontWeight: 650, opacity: local >= sitRows.length - 1 ? 0.45 : 1,
          }}>Next <ChevronRight size={14} /></button>
        </div>
        <div className="tgw-pair tgw-a tgw-a2" style={{
          display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
          height: "calc(100vh - 320px)", maxHeight: "calc(100vh - 320px)",
        }}>
          <GPanel t={t} style={{
            margin: 0, background: t.isDark ? ppr.card : "#F7F9FC",
            display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", height: "100%",
          }}>
            <SectionBar t={t} tone="violet" icon={<FileText size={16} strokeWidth={2.2} />} title="What happened" info="attention"
              bits={[
                { icon: <Users size={12} strokeWidth={2.2} />, label: sit.name || "Someone" },
                { icon: <Check size={12} strokeWidth={2.2} />, label: "this sitting" },
              ]} />
            <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0 }}>
            <WorkWriteup
              key={sitKey}
              t={t}
              sf={sf}
              tone={k.blue}
              compact
              userId={sit.userId}
              date={sit.day}
              title={sit.what || sit.title || themeTitle}
              detail={`${sit.name || ""} on ${name}`.trim()}
              blockStarts={[sit.startMs]}
            />
            </div>
          </GPanel>
          <aside style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", gap: 12 }}>
            <GPanel t={t} style={{ margin: 0, flexShrink: 0 }}>
              <SectionBar t={t} tone="blue" icon={<Clock size={16} strokeWidth={2.2} />} title="On the clock" info="rhythm"
                bits={[{ icon: <Clock size={12} strokeWidth={2.2} />, label: clock ? `${clock.mins} min` : "no clock times" }]} />
              {clock ? (
                <div style={{ padding: "12px 14px", borderRadius: 12, background: ppr.well }}>
                  <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>This sitting</div>
                  <div style={{ marginTop: 2, fontSize: 26, fontWeight: 750, letterSpacing: "-0.8px", fontVariantNumeric: "tabular-nums" }}>
                    {fmtMins(clock.mins)}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 14, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
                    {clock.t0} → {clock.t1}
                  </div>
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: t.c.textTertiary }}>This sitting has no clock times.</p>
              )}
            </GPanel>
            {sitRows.length > 1 ? (
              <GPanel t={t} style={{
                margin: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden",
              }}>
                <SectionBar t={t} tone="teal" icon={<Layers size={16} strokeWidth={2.2} />} title="Also this piece" info="focus"
                  bits={[{ icon: <Layers size={12} strokeWidth={2.2} />, label: `${sitRows.length} sittings` }]} />
                <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0 }}>
                  <SittingTiles t={t} rows={sitRows} inkMap={inkMap} currentId={sitKey} />
                </div>
              </GPanel>
            ) : null}
            <LogDoorBtn t={t} href={workLogTo(logUser, logDay)} title="Work summary · insights" sub={`${firstName(sit.name)}’s day`} />
          </aside>
        </div>
      </>
    );
  }

  return (
    <div className="tgw-pair tgw-a tgw-a2" style={{
      display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
    }}>
      <GPanel t={t} style={{
        margin: 0, display: "flex", flexDirection: "column", minHeight: 0,
        maxHeight: "calc(100vh - 268px)", overflow: "hidden",
      }}>
        <SectionBar t={t} tone="violet" icon={<FileText size={16} strokeWidth={2.2} />} title="What happened" info="focus"
          bits={[
            { icon: <Layers size={12} strokeWidth={2.2} />, label: sitRows.length ? `${sitRows.length} sitting${sitRows.length === 1 ? "" : "s"}` : "this piece" },
            { icon: <Clock size={12} strokeWidth={2.2} />, label: `on ${name}` },
          ]} />
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {error && !headline ? <p style={{ margin: 0, fontSize: 14, color: t.status?.negative || t.c.textSecondary }}>{error}</p> : null}
          {empty && !headline ? <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: t.c.textSecondary }}>{empty}</p> : null}
          {!headline && running ? <WaitLine t={t} text="Writing this piece of work…" /> : null}
          <OutcomeGlance t={t} outcomes={outcomes} insights={beats} />
          {sitRows.length ? (
            <SittingOverview t={t} rows={sitRows} inkMap={inkMap} headline={headline} beats={beats} />
          ) : (
            <GlancePoints t={t} headline={headline} beats={beats} />
          )}
          {!running && !headline && !empty && !sitRows.length ? (
            <p style={{ margin: 0, fontSize: 13.5, color: t.c.textSecondary, lineHeight: 1.55 }}>
              Time is on this piece. No sitting we can write up yet.
            </p>
          ) : null}
        </div>
      </GPanel>
      <aside>
        <GPanel t={t} style={{ marginBottom: 12 }}>
          <SectionBar t={t} tone="blue" icon={<Users size={16} strokeWidth={2.2} />} title="Who did this" info="people"
            bits={[{ icon: <Users size={12} strokeWidth={2.2} />, label: people.length ? `${people.length} ${people.length === 1 ? "person" : "people"}` : "no one yet" }]} />
          {people.length ? (
            <div className="tgw-list tgw-alerts-scroll" style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 220 }}>
              {people.map((p, i) => (
                  <button
                    key={p.userId || i}
                    type="button"
                    className="tgw-row"
                    onClick={() => openMember?.({ userId: p.userId, firstName: p.name })}
                    style={{
                      display: "grid", gridTemplateColumns: "36px 1fr auto", gap: 10, alignItems: "center",
                      width: "100%", textAlign: "left", border: 0, padding: "10px 10px", borderRadius: 12,
                      cursor: "pointer", fontFamily: "inherit", color: "inherit", background: "transparent",
                    }}
                  >
                    <span aria-hidden="true" style={{
                      width: 36, height: 36, borderRadius: 10, overflow: "hidden",
                      display: "grid", placeItems: "center",
                    }}>
                      <Avatar user={p} name={p.name} size={36} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{
                        display: "block", fontSize: 13.5, fontWeight: 650,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{p.name}</span>
                      <span style={{ display: "block", marginTop: 2, fontSize: 12, color: t.c.textTertiary }}>
                        {p.sittings ? `${p.sittings} sitting${p.sittings === 1 ? "" : "s"}` : `on ${name}`}
                      </span>
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 650, fontVariantNumeric: "tabular-nums", color: t.c.textSecondary }}>
                      {fmtMins(p.minutes)}
                    </span>
                  </button>
              ))}
            </div>
          ) : running ? (
            <SkeletonRows count={3} height={36} />
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary }}>No one named on this piece yet.</p>
          )}
        </GPanel>
        <LogDoorBtn
          t={t}
          href={workLogTo(logUser, logDay)}
          title="Work summary · insights"
          sub={logUser ? `${firstName(lead?.name)}’s day` : "That person’s day"}
        />
        <button type="button" onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 6, width: "100%", marginTop: 8,
          border: 0, background: "none", color: t.c.textSecondary, fontWeight: 550,
          cursor: "pointer", fontFamily: "inherit", padding: "8px 4px", fontSize: 13,
        }}>
          <ChevronLeft size={14} />
          <span>All work on {name}</span>
        </button>
      </aside>
    </div>
  );
}

export function ProjectPersonStory({
  t, projectId, range, name, contributor, openPerson, onBack,
  story: storyProp, inkMap, hotId, onHot,
}) {
  const k = inks(t);
  const sf = useSurface();
  const navigate = useNavigate();
  const userId = contributor.userId || contributor.id;
  const insightsTo = (() => {
    const { startDate } = fmtDay(range);
    return workLogTo(userId, startDate);
  })();
  const personName = `${contributor.firstName || ""} ${contributor.lastName || ""}`.trim() || contributor.email || "This person";
  /* Frame-owned stream when provided; the hook still runs but fetches
     nothing with projectId null, so the SSE never doubles up. */
  const own = useProjectStory({ projectId: storyProp ? null : projectId, range, userId });
  const story = storyProp || own;

  /* The sitting is a query param — linkable, refreshable, Back-safe. */
  const { sitKey, openSit, closeSit } = useSitParam();
  const allSits = (story.facts?.sittings || []).concat(story.sittings || []);
  const sit = sitKey ? allSits.find((s) => sitId(s) === sitKey) || null : null;

  const first = personName.split(" ")[0];
  const ppr = paper(t);

  if (sit) {
    const sibs = [...allSits].sort((a, b) => (Number(a.startMs) || 0) - (Number(b.startMs) || 0));
    const local = Math.max(0, sibs.findIndex((s) => sitId(s) === sitKey));
    const jump = (dir) => { const n = sibs[local + dir]; if (n) openSit(n); };
    const face = honestFace(sit);
    const mins = face.mins;
    const t0 = face.range ? face.range.split(" → ")[0] : "";
    const t1 = face.range ? face.range.split(" → ")[1] : "";
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <button type="button" onClick={closeSit} style={{
            display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
            borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
            color: t.c.text, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 650,
          }}><ChevronLeft size={14} /> {first} on {name}</button>
          <span style={{ fontSize: 13, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
            Sitting {sibs.length ? local + 1 : "—"} of {sibs.length || "—"}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" disabled={local <= 0} onClick={() => jump(-1)} style={{
            display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
            borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
            color: local <= 0 ? t.c.textTertiary : t.c.text, cursor: local <= 0 ? "default" : "pointer",
            fontFamily: "inherit", fontSize: 12.5, fontWeight: 650, opacity: local <= 0 ? 0.45 : 1,
          }}><ChevronLeft size={14} /> Prev</button>
          <button type="button" disabled={local >= sibs.length - 1} onClick={() => jump(1)} style={{
            display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
            borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
            color: local >= sibs.length - 1 ? t.c.textTertiary : t.c.text,
            cursor: local >= sibs.length - 1 ? "default" : "pointer",
            fontFamily: "inherit", fontSize: 12.5, fontWeight: 650, opacity: local >= sibs.length - 1 ? 0.45 : 1,
          }}>Next <ChevronRight size={14} /></button>
        </div>
        <div className="tgw-pair tgw-a tgw-a2" style={{
          display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
          height: "calc(100vh - 320px)", maxHeight: "calc(100vh - 320px)",
        }}>
          <GPanel t={t} style={{
            margin: 0, background: t.isDark ? ppr.card : "#F7F9FC",
            display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", height: "100%",
          }}>
            <SectionBar t={t} tone="violet" icon={<FileText size={16} strokeWidth={2.2} />} title="What happened" info="attention"
              bits={[
                { icon: <Clock size={12} strokeWidth={2.2} />, label: cleanLine(sit.what || sit.title) || "This sitting" },
                { icon: <Check size={12} strokeWidth={2.2} />, label: `${first} · ${name}` },
              ]} />
            <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0 }}>
            <WorkWriteup
              key={sitKey}
              t={t}
              sf={sf}
              tone={k.blue}
              compact
              userId={sit.userId}
              date={sit.day}
              title={sit.what || sit.title || name}
              detail={`${personName} on ${name}`}
              blockStarts={[sit.startMs]}
            />
            </div>
          </GPanel>
          <aside style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", gap: 12 }}>
            <GPanel t={t} style={{ margin: 0, flexShrink: 0 }}>
              <SectionBar t={t} tone="blue" icon={<Clock size={16} strokeWidth={2.2} />} title="On the clock" info="rhythm"
                bits={[{ icon: <Clock size={12} strokeWidth={2.2} />, label: mins ? `${mins} min` : "no clock times" }]} />
              {mins ? (
                <div style={{ padding: "12px 14px", borderRadius: 12, background: ppr.well }}>
                  <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>This sitting</div>
                  <div style={{ marginTop: 2, fontSize: 26, fontWeight: 750, letterSpacing: "-0.8px", fontVariantNumeric: "tabular-nums" }}>
                    {fmtMins(mins)}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 14, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
                    {t0}{t1 ? ` → ${t1}` : ""}
                  </div>
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: t.c.textTertiary }}>This sitting has no clock times.</p>
              )}
            </GPanel>
            {sibs.length > 1 ? (
              <GPanel t={t} style={{
                margin: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden",
              }}>
                <SectionBar t={t} tone="teal" icon={<Layers size={16} strokeWidth={2.2} />} title={`Also ${first}`} info="focus"
                  bits={[{ icon: <Layers size={12} strokeWidth={2.2} />, label: `${sibs.length} sittings` }]} />
                <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0 }}>
                  <SittingTiles t={t} rows={sibs} onOpen={openSit} inkMap={inkMap} currentId={sitKey} />
                </div>
              </GPanel>
            ) : null}
            <LogDoorBtn t={t} href={insightsTo} title="Work summary · insights" sub={`${first}’s day`} />
          </aside>
        </div>
      </>
    );
  }

  if (sitKey && story.running) {
    return (
      <GPanel t={t} style={{ margin: 0 }}>
        <SectionBar t={t} tone="blue" icon={<Clock size={16} strokeWidth={2.2} />} title="Opening this sitting" info="focus" />
        <SkeletonRows />
      </GPanel>
    );
  }

  const rows = story.sittings.length ? story.sittings : (story.facts?.sittings || []);

  return (
    <div className="tgw-pair tgw-a tgw-a2" style={{
      display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
    }}>
      <GPanel t={t} style={{
        margin: 0, display: "flex", flexDirection: "column", minHeight: 0,
        maxHeight: "calc(100vh - 268px)", overflow: "hidden",
      }}>
        <SectionBar t={t} tone="violet" icon={<Clock size={16} strokeWidth={2.2} />} title="Their sittings" info="focus"
          bits={[
            { icon: <Clock size={12} strokeWidth={2.2} />, label: `${rows.length} sitting${rows.length === 1 ? "" : "s"}` },
            { icon: <Layers size={12} strokeWidth={2.2} />, label: `on ${name}` },
          ]} />
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", margin: "0 -6px", padding: "0 6px 4px" }}>
          <StoryLede t={t} lede={story.lede} beats={story.beats} running={story.running} empty={story.empty} error={story.error} compact />
          {story.running && !rows.length ? <SkeletonRows count={3} /> : null}
          {rows.length ? (
            <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: story.lede || story.beats?.length ? 14 : 0 }}>
              {[...rows].sort((a, b) => (Number(a.startMs) || 0) - (Number(b.startMs) || 0)).map((st, i) => {
                const ink = k[(inkMap && inkMap[sitId(st)]) || "blue"] || k.blue;
                const face = honestFace(st);
                const line = cleanLine(st.what || st.title) || "A sitting";
                return (
                  <button
                    key={sitId(st) || i}
                    type="button"
                    className="tgw-row"
                    onClick={() => openSit(st)}
                    style={{
                      display: "grid", gridTemplateColumns: "48px 1fr 18px", gap: 12, alignItems: "center",
                      width: "100%", textAlign: "left", border: 0, padding: "11px 12px", borderRadius: 14,
                      cursor: "pointer", fontFamily: "inherit", color: "inherit",
                      background: t.isDark ? ppr.well : "#fff",
                      boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                    }}
                  >
                    <span aria-hidden="true" style={{
                      width: 48, height: 48, borderRadius: 11, flexShrink: 0,
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      background: ink, color: "#fff", gap: 1,
                    }}>
                      <span style={{
                        fontSize: 13, fontWeight: 750, fontVariantNumeric: "tabular-nums",
                        letterSpacing: "-0.4px", whiteSpace: "nowrap", lineHeight: 1,
                      }}>{face.hm || line.slice(0, 2)}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 650, opacity: 0.82, whiteSpace: "nowrap", lineHeight: 1.2,
                      }}>{face.mins ? fmtMins(face.mins) : ""}</span>
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{
                        ...clamp2, fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px",
                        lineHeight: 1.35, color: t.c.text,
                      }}>{line}</span>
                      <span style={{
                        display: "block", marginTop: 5, fontSize: 12.5, color: t.c.textSecondary,
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {sittingMeta(st)}
                      </span>
                    </span>
                    <span style={{ color: t.c.textTertiary, display: "grid" }}><ChevronRight size={16} /></span>
                  </button>
                );
              })}
            </div>
          ) : (!story.running ? (
            <p style={{ margin: "8px 0 0", fontSize: 13.5, color: t.c.textSecondary, lineHeight: 1.55 }}>
              Time is on this project. No sitting we can write up yet.
            </p>
          ) : null)}
        </div>
      </GPanel>

      <aside>
        <GPanel t={t} style={{ marginBottom: 12 }}>
          <SectionBar t={t} tone="blue" icon={<Layers size={16} strokeWidth={2.2} />} title="Pieces they touched" info="attention"
            bits={[{ icon: <Layers size={12} strokeWidth={2.2} />, label: story.themes.length ? `${story.themes.length}` : "none yet" }]} />
          {story.themes.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {story.themes.map((th, i) => {
                const ink = k[["blue", "violet", "gold", "pink"][i % 4]] || k.blue;
                const mine = rows.filter((s) => s.theme && (s.theme === th.key || s.theme === th.title));
                const mins = mine.reduce((n, s) => n + (Number(s.minutes) || 0), 0);
                return (
                  <div key={th.key} style={{
                    display: "grid", gridTemplateColumns: "32px 1fr", gap: 10, alignItems: "start",
                    padding: "12px 12px", borderRadius: 14,
                    background: t.isDark ? ppr.well : "#fff",
                    boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                  }}>
                    <span aria-hidden="true" style={{
                      width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                      display: "grid", placeItems: "center", background: ink, color: "#fff",
                    }}><Layers size={14} strokeWidth={2.2} /></span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 650, letterSpacing: "-0.15px", lineHeight: 1.3 }}>
                        {cleanLine(th.title)}
                      </span>
                      {th.what ? (
                        <span style={{ ...clamp2, marginTop: 4, fontSize: 12.5, lineHeight: 1.45, color: t.c.textSecondary }}>
                          {cleanLine(th.what)}
                        </span>
                      ) : null}
                      {mins || mine.length ? (
                        <span style={{ display: "block", marginTop: 6, fontSize: 12, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                          {mine.length ? `${mine.length} sitting${mine.length === 1 ? "" : "s"}` : ""}
                          {mins ? `${mine.length ? " · " : ""}${fmtMins(mins)}` : ""}
                        </span>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary }}>No named pieces yet.</p>
          )}
        </GPanel>
        <button type="button" onClick={() => navigate(insightsTo)} style={{
          display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
          padding: "15px 14px", borderRadius: 14, cursor: "pointer", marginBottom: 10,
          fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
        }}>
          <span aria-hidden="true" style={{
            width: 40, height: 40, borderRadius: 11, flexShrink: 0,
            display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
          }}><BookOpen size={18} strokeWidth={2.2} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 650 }}>Work summary · insights</span>
            <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>{first}’s day</span>
          </span>
          <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
        </button>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "2px 2px 0" }}>
          <button type="button" onClick={() => openPerson(String(userId), personName)} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
            border: 0, background: "none", color: t.c.textSecondary, fontWeight: 550,
            cursor: "pointer", fontFamily: "inherit", padding: "8px 4px", fontSize: 13,
          }}>
            <span>{first}’s full day</span>
            <ChevronRight size={14} />
          </button>
          <button type="button" onClick={onBack} style={{
            display: "flex", alignItems: "center", gap: 6, width: "100%",
            border: 0, background: "none", color: t.c.textSecondary, fontWeight: 550,
            cursor: "pointer", fontFamily: "inherit", padding: "8px 4px", fontSize: 13,
          }}>
            <ChevronLeft size={14} />
            <span>Everyone on {name}</span>
          </button>
        </div>
      </aside>
    </div>
  );
}

/* ── the digest's KPI ledger ─────────────────────────────────────────────
   Four boxes become one strip that judges, not just counts: an active-vs-
   idle split bar, and idle exceeding active gets an amber flag instead of
   hiding as plain grey text. */

const AVATAR_INKS = ["blue", "violet", "gold", "pink"];

export function ProjectLedger({ t, trackedMinutes, activeMinutes, idleMinutes, people, sittingCount }) {
  const k = inks(t);
  const ppr = paper(t);
  const tracked = Math.max(0, Number(trackedMinutes) || 0);
  const active = Math.max(0, Number(activeMinutes) || 0);
  const idle = Math.max(0, Number(idleMinutes) || 0);
  const activePct = tracked ? Math.round((active / tracked) * 100) : 0;
  const idleWins = idle > active && tracked > 0;
  const idleColor = t.isDark ? k.gold : k.gold;

  const cell = { flex: 1, minWidth: 120, paddingRight: 18, marginRight: 18, borderRight: `1px solid ${t.c.border}` };
  const lastCell = { ...cell, borderRight: "none", marginRight: 0, paddingRight: 0 };
  const lbl = {
    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em",
    textTransform: "uppercase", color: t.c.textTertiary, marginBottom: 3,
  };
  const val = { fontSize: 19, fontWeight: 700, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" };
  const sub = { fontSize: 11.5, color: t.c.textTertiary, marginTop: 2 };

  return (
    <GPanel t={t} className="tgw-card tgw-a tgw-a2" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", rowGap: 14 }}>
        <div style={cell}>
          <div style={lbl}>Tracked</div>
          <div style={val}>{fmtMins(tracked)}</div>
          <div style={sub}>{sittingCount ? `across ${sittingCount} sitting${sittingCount === 1 ? "" : "s"}` : "in this window"}</div>
        </div>
        <div style={cell}>
          <div style={lbl}>Active</div>
          <div style={val}>{fmtMins(active)}</div>
          <div style={sub}>{tracked ? `${activePct}% of tracked` : "—"}</div>
        </div>
        <div style={cell}>
          <div style={lbl}>Idle</div>
          <div style={val}>{fmtMins(idle)}</div>
          {idleWins ? (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4, marginTop: 3,
              fontSize: 10.5, fontWeight: 700, color: idleColor,
              background: `${idleColor}20`, boxShadow: `0 0 0 1px ${idleColor}55`,
              borderRadius: 999, padding: "2px 9px",
            }}>▲ more idle than active</span>
          ) : (
            <div style={sub}>inside this project</div>
          )}
        </div>
        <div style={lastCell}>
          <div style={lbl}>People</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
            {(people || []).slice(0, 3).map((p, i) => (
              <span key={p.userId || i} title={p.name} style={{
                width: 26, height: 26, borderRadius: 99, flexShrink: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: "#fff",
                background: k[AVATAR_INKS[i % AVATAR_INKS.length]] || k.blue,
              }}>{(p.name || "?").trim().charAt(0).toUpperCase()}</span>
            ))}
            {(people || []).length > 3 ? (
              <span style={{
                width: 26, height: 26, borderRadius: 99, flexShrink: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, color: t.c.textSecondary, background: ppr.well,
              }}>+{people.length - 3}</span>
            ) : null}
            {(people || [])[0] ? (
              <span style={{ fontSize: 11.5, color: t.c.textTertiary }}>
                {people[0].name.split(" ")[0]} led
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {tracked > 0 ? (
        <>
          <div aria-hidden="true" style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginTop: 14 }}>
            <span style={{ width: `${Math.min(100, Math.round((active / tracked) * 100))}%`, background: k.blue }} />
            <span style={{ flex: 1, background: `${idleColor}88` }} />
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 7, fontSize: 11.5, color: t.c.textTertiary }}>
            <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: k.blue, marginRight: 5 }} />Active {fmtMins(active)}</span>
            <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: `${idleColor}88`, marginRight: 5 }} />Idle {fmtMins(idle)}</span>
          </div>
        </>
      ) : null}
    </GPanel>
  );
}

/* ── the digest's story threads ──────────────────────────────────────────
   Flat numbered boxes become a lede plus threads: each piece of work the
   stream names gets its beats attached by word overlap (a presentational
   grouping of the model's own sentences — nothing rewritten), an ink dot
   matching its timeline blocks, real minutes, and a direct door. Beats
   that match no theme stay under the lede so nothing is dropped. */

function tokens(text) {
  return String(text || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3);
}

export function buildThreads({ beats, themes, sittings, inkMap }) {
  const ths = (themes || []).map((th, i) => {
    const sits = (sittings || []).filter((s) => s.theme === th.key);
    const minutes = sits.reduce((n, s) => n + (Number(s.minutes) || 0), 0);
    const segIds = sits.map((s) => sitId(s));
    const ink = (sits.length && inkMap?.[segIds[0]]) || SEG_INK_NAMES[i % SEG_INK_NAMES.length];
    return { ...th, sits, minutes, segIds, ink, bullets: [], toks: tokens(`${th.title} ${th.what}`) };
  });
  const loose = [];
  for (const beat of beats || []) {
    const bt = tokens(beat);
    let best = null;
    let bestScore = 0;
    for (const th of ths) {
      const score = bt.filter((w) => th.toks.includes(w)).length;
      if (score > bestScore) { best = th; bestScore = score; }
    }
    if (best && bestScore >= 2) best.bullets.push(beat);
    else loose.push(beat);
  }
  return { threads: ths, loose };
}

export function ThreadedStory({ t, story, inkMap, openTheme, onThreadHover }) {
  const k = inks(t);
  const { threads, loose } = React.useMemo(
    () => buildThreads({ beats: story.beats, themes: story.themes, sittings: story.sittings, inkMap }),
    [story.beats, story.themes, story.sittings, inkMap],
  );

  if (story.error && !story.lede && !story.beats.length) {
    return <div style={{ fontSize: 14, color: t.status?.negative || t.c.textSecondary }}>{story.error}</div>;
  }
  if (story.empty && !story.lede && !story.beats.length) {
    return <div style={{ fontSize: 14.5, lineHeight: 1.55, color: t.c.textSecondary }}>{story.empty}</div>;
  }
  if (!story.lede && !story.beats.length && !threads.length) {
    return story.running
      ? <><WaitLine t={t} text="Reading what was actually done…" /><SkeletonRows count={3} /></>
      : <div style={{ fontSize: 13.5, color: t.c.textSecondary }}>No written picture yet.</div>;
  }

  /* No named pieces of work → the flat list is the honest layout. */
  if (!threads.length) {
    return <StoryLede t={t} lede={story.lede} beats={story.beats} running={story.running} empty={story.empty} error={story.error} />;
  }

  const ppr = paper(t);
  return (
    <div>
      {story.lede ? (
        <p style={{
          margin: "0 0 12px", fontSize: 15.5, fontWeight: 650, lineHeight: 1.4,
          letterSpacing: "-0.2px", color: t.c.text,
        }}>{story.lede}</p>
      ) : null}
      {loose.length ? (
        <div style={{ margin: "0 0 12px", display: "flex", flexDirection: "column", gap: 6 }}>
          {loose.map((b, i) => (
            <p key={i} style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: t.c.textSecondary }}>{b}</p>
          ))}
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {threads.map((th) => {
          const ink = k[th.ink] || k.violet;
          const lines = th.bullets.length ? th.bullets : (th.what ? [th.what] : []);
          return (
            <button
              key={th.key}
              type="button"
              className="tgw-row"
              onClick={openTheme ? () => openTheme(th) : undefined}
              onMouseEnter={onThreadHover ? () => onThreadHover(th.segIds) : undefined}
              onMouseLeave={onThreadHover ? () => onThreadHover(null) : undefined}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "13px 14px", borderRadius: 12, border: 0,
                background: ppr.well, cursor: openTheme ? "pointer" : "default",
                fontFamily: "inherit", color: "inherit",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span aria-hidden="true" style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  display: "grid", placeItems: "center", background: ink, color: "#fff",
                }}><Layers size={15} strokeWidth={2.2} /></span>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 14, fontWeight: 650, letterSpacing: "-0.2px",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{th.title}</span>
                {th.minutes ? (
                  <span style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: t.c.textSecondary, flexShrink: 0 }}>
                    {fmtMins(th.minutes)}
                  </span>
                ) : null}
                {openTheme ? <span style={{ color: ink, display: "grid" }}><ChevronRight size={16} /></span> : null}
              </div>
              {lines.length ? (
                <div style={{ margin: "8px 0 0 42px", display: "flex", flexDirection: "column", gap: 5 }}>
                  {lines.slice(0, 3).map((b, i) => (
                    <p key={i} style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: t.c.textSecondary }}>{b}</p>
                  ))}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
      {story.running ? <WaitLine t={t} text="Still arriving…" /> : null}
    </div>
  );
}

export function ProjectDigestStory({
  t, projectId, range, name, periodLabel, contributors, trackedMinutes, activeMinutes, idleMinutes,
  openMember, openTheme, story: storyProp, loading, inkMap, onThreadHover,
}) {
  const own = useProjectStory({ projectId: storyProp ? null : projectId, range });
  const story = storyProp || own;
  const people = (contributors || []).map((c) => ({
    userId: c.userId,
    name: `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.email || "Member",
    totalMinutes: Math.round((Number(c.totalHours) || 0) * 60),
    activeMinutes: Math.round((Number(c.totalHours) || 0) * 60),
    idleMinutes: 0,
  }));
  const sittingCount = Math.max(story.sittings.length, story.facts?.sittings?.length || 0);
  const k = inks(t);
  const ranked = [...people].sort((a, b) => (b.totalMinutes || 0) - (a.totalMinutes || 0));

  return (
    <div className="tgw-pair tgw-a tgw-a2" style={{
      display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
    }}>
      <GPanel t={t} style={{
        margin: 0, display: "flex", flexDirection: "column", minHeight: 0,
        maxHeight: "calc(100vh - 268px)", overflow: "hidden",
      }}>
        <SectionBar t={t} tone="violet" icon={<Layers size={16} strokeWidth={2.2} />} title="What happened" info="focus"
          bits={[
            { icon: <Layers size={12} strokeWidth={2.2} />, label: `${story.themes.length || 0} piece${story.themes.length === 1 ? "" : "s"}` },
            { icon: <Clock size={12} strokeWidth={2.2} />, label: `${sittingCount} sitting${sittingCount === 1 ? "" : "s"}` },
          ]} />
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", margin: "0 -6px", padding: "0 6px 4px" }}>
          <ThreadedStory t={t} story={story} inkMap={inkMap} openTheme={openTheme} onThreadHover={onThreadHover} />
        </div>
      </GPanel>

      <aside style={{ display: "flex", minHeight: 0, maxHeight: "calc(100vh - 268px)" }}>
        <GPanel t={t} style={{ margin: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <SectionBar t={t} tone="blue" icon={<Users size={16} strokeWidth={2.2} />} title="Who was on it" info="people"
            bits={[{ icon: <Users size={12} strokeWidth={2.2} />, label: people.length ? `${people.length} people` : "no one yet" }]} />
          {ranked.length ? (
            <div className="tgw-list tgw-alerts-scroll" style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
              {ranked.map((p, i) => {
                const ink = k[["blue", "violet", "gold", "pink"][i % 4]] || k.blue;
                const ini = (p.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <button
                    key={p.userId || i}
                    type="button"
                    className="tgw-row"
                    onClick={() => openMember?.({ userId: p.userId, firstName: p.name })}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      textAlign: "left", border: 0, padding: "8px 6px", borderRadius: 10,
                      cursor: "pointer", fontFamily: "inherit", color: "inherit", background: "transparent",
                    }}
                  >
                    <span aria-hidden="true" style={{
                      width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                      display: "grid", placeItems: "center",
                      fontSize: 11, fontWeight: 700, background: ink, color: "#fff",
                    }}>{ini}</span>
                    <span style={{
                      flex: 1, minWidth: 0, fontSize: 13, fontWeight: 550,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{p.name}</span>
                    <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", color: t.c.textSecondary }}>
                      {fmtMins(p.totalMinutes)}
                    </span>
                    <span style={{ color: k.blue, display: "grid" }}><ChevronRight size={15} /></span>
                  </button>
                );
              })}
            </div>
          ) : loading ? (
            <SkeletonRows count={3} height={36} />
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary }}>No one tracked time on {name} in this window.</p>
          )}
        </GPanel>
      </aside>
    </div>
  );
}
